import React, { useEffect, useState } from "react";
import api, { fmtMoney } from "@/lib/api";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { Users, ArrowDownLeft, ArrowUpRight, Clock } from "lucide-react";

export default function AdminDashboard() {
  const [s, setS] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setS(data)).catch(() => {});
    const t = setInterval(() => api.get("/admin/stats").then(({ data }) => setS(data)).catch(() => {}), 8000);
    return () => clearInterval(t);
  }, []);

  if (!s) return <div className="py-20 text-center text-slate-400 animate-pulse">Loading…</div>;

  const cards = [
    { label: "Total Users", val: s.total_users, money: false, icon: Users, color: "from-blue-600 to-indigo-700" },
    { label: "Total Pay-In", val: s.total_payin, money: true, icon: ArrowDownLeft, color: "from-emerald-600 to-teal-700" },
    { label: "Total Payout", val: s.total_payout, money: true, icon: ArrowUpRight, color: "from-violet-600 to-purple-700" },
    { label: "Pending Payouts", val: s.pending_payout, money: true, icon: Clock, color: "from-amber-500 to-orange-600" },
  ];

  const pie = [
    { name: "Collection", value: s.total_collection_balance, color: "#0F62FE" },
    { name: "Payout", value: s.total_payout_balance, color: "#10B981" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Admin overview</h1>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <div key={i} className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${c.color} text-white p-5 shadow-lg`} data-testid={`admin-stat-${i}`}>
            <div className="absolute -right-5 -top-5 h-20 w-20 rounded-full bg-white/10" />
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/90">{c.label}</span>
              <c.icon className="h-5 w-5 text-white/80" />
            </div>
            <div className="mt-5 font-mono-data text-2xl font-bold">{c.money ? fmtMoney(c.val) : c.val}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <h3 className="font-heading font-semibold text-slate-900 dark:text-white">Pay-In vs Payout — last 7 days</h3>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={s.series}>
                <defs>
                  <linearGradient id="gi" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0F62FE" stopOpacity={0.4} /><stop offset="100%" stopColor="#0F62FE" stopOpacity={0} /></linearGradient>
                  <linearGradient id="go" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.4} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" opacity={0.3} />
                <XAxis dataKey="date" tickFormatter={(d) => d?.slice(5)} fontSize={11} stroke="#94a3b8" />
                <YAxis fontSize={11} stroke="#94a3b8" width={45} />
                <Tooltip formatter={(v) => fmtMoney(v)} />
                <Legend />
                <Area type="monotone" dataKey="payin" name="Pay-In" stroke="#0F62FE" strokeWidth={2} fill="url(#gi)" />
                <Area type="monotone" dataKey="payout" name="Payout" stroke="#10B981" strokeWidth={2} fill="url(#go)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <h3 className="font-heading font-semibold text-slate-900 dark:text-white">Wallet distribution</h3>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                  {pie.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtMoney(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
